import {FC, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {QRCode} from 'react-qrcode-logo'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {ANDROID_APP, androidAppAvailable, offersAndroidApp} from '../core/androidApp'
import {BROWSER_NAMES, IN_APP_NAMES, InstallEnvironment, OS_NAMES} from '../core/installEnvironment'
import {GUIDE_CHOICES, installGuideFor} from '../core/installGuides'
import {InstallOutcome} from '../core/installPrompt'
import {installedAppIcon} from '../core/useInstallState'
import {AndroidAppCard} from './AndroidAppCard'
import {GuideLine, InstallGuideSteps} from './InstallGuideSteps'

export const environmentLabel = (env: InstallEnvironment) =>
  env.inApp
    ? `${OS_NAMES[env.os]} · داخل ${IN_APP_NAMES[env.inApp]}`
    : `${OS_NAMES[env.os]} · ${BROWSER_NAMES[env.browser]}`

const BENEFITS = [
  {icon: 'la-bolt', text: 'باز شدن با یک لمس یا کلیک'},
  {icon: 'la-expand', text: 'تمام‌صفحه، بدون نوار مرورگر'},
  {icon: 'la-sync', text: 'همیشه به‌روز، بی‌نیاز از نصب دوباره'},
]

type CopyState = 'idle' | 'copied' | 'failed'

type Props = {
  show: boolean
  appName: string
  detected: InstallEnvironment
  canPrompt: boolean
  runningInstalled: boolean
  installedNow: boolean
  /** Chromium says the panel is installed on this device though this is a browser tab. */
  installedElsewhere?: boolean
  onPrompt: () => Promise<InstallOutcome>
  onClose: () => void
}

/**
 * The install guide: the device and browser it detected (another can be picked), the browser's
 * one-click install where it offers one, and otherwise the exact steps of that browser, with its
 * own buttons drawn in. On a computer a QR code takes the panel to a phone.
 */
export const InstallAppModal: FC<Props> = ({
  show,
  appName,
  detected,
  canPrompt,
  runningInstalled,
  installedNow,
  installedElsewhere,
  onPrompt,
  onClose,
}) => {
  const [choiceId, setChoiceId] = useState<string>('detected')
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<InstallOutcome>()
  const [copy, setCopy] = useState<CopyState>('idle')

  useEffect(() => {
    if (!show) return
    setChoiceId('detected')
    setPicking(false)
    setOutcome(undefined)
    setCopy('idle')
  }, [show])

  const env = useMemo(
    () =>
      choiceId === 'detected'
        ? detected
        : GUIDE_CHOICES.find((choice) => choice.id === choiceId)?.env ?? detected,
    [choiceId, detected]
  )
  const guide = installGuideFor(env)
  const onDetected = choiceId === 'detected'
  const oneClick = onDetected && guide.support === 'prompt' && canPrompt
  const panelUrl = `${window.location.origin}/`
  const installed = runningInstalled || installedNow || outcome === 'accepted'
  // An Android phone gets the Android app first; installing the web app stays as the other way.
  const androidApp = offersAndroidApp(env)

  const install = async () => {
    setBusy(true)
    try {
      setOutcome(await onPrompt())
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(panelUrl)
      setCopy('copied')
    } catch {
      setCopy('failed')
    }
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      fullscreen='sm-down'
      scrollable
      dialogClassName='ia-modal'
      aria-labelledby='ia-title'
    >
      <Modal.Body className='ia-modal__body'>
        <button type='button' className='ia-modal__close' onClick={onClose} aria-label='بستن'>
          <i className='las la-times' aria-hidden='true' />
        </button>

        <header className='ia-head'>
          <img
            className='ia-head__icon'
            src={toAbsoluteUrl(installedAppIcon())}
            alt=''
            width={64}
            height={64}
          />
          <div>
            <h2 id='ia-title' className='ia-title'>
              نصب برنامهٔ {appName}
            </h2>
            <ul className='ia-benefits' aria-label='چرا نصب کنم؟'>
              {BENEFITS.map((benefit) => (
                <li key={benefit.icon}>
                  <i className={`las ${benefit.icon}`} aria-hidden='true' />
                  {benefit.text}
                </li>
              ))}
            </ul>
          </div>
        </header>

        {runningInstalled ? (
          <div className='ia-state ia-state--done' role='status'>
            <i className='las la-check-circle' aria-hidden='true' />
            برنامه روی همین دستگاه نصب است و همین حالا در آن هستید.
          </div>
        ) : installed ? (
          <div className='ia-state ia-state--done' role='status'>
            <i className='las la-check-circle' aria-hidden='true' />
            برنامه نصب شد. از این پس آن را از {guide.where} باز کنید.
          </div>
        ) : (
          <>
            {installedElsewhere && (
              <div className='ia-state' role='status'>
                <i className='las la-info-circle' aria-hidden='true' />
                به نظر می‌رسد برنامه روی این دستگاه نصب است؛ آن را از {guide.where} باز کنید.
              </div>
            )}

            <div className='ia-device'>
              <span className='ia-device__label'>
                {onDetected ? 'دستگاه شما:' : 'راهنمای:'}{' '}
                <strong>
                  <bdi>{environmentLabel(env)}</bdi>
                </strong>
              </span>
              {!picking && (
                <button
                  type='button'
                  className='btn btn-link btn-sm ia-device__change'
                  onClick={() => setPicking(true)}
                >
                  دستگاه یا مرورگر دیگری دارید؟
                </button>
              )}
            </div>
            {picking && (
              <div className='ia-pick'>
                <label htmlFor='ia-choice' className='form-label'>
                  دستگاه و مرورگر
                </label>
                <select
                  id='ia-choice'
                  className='form-select form-select-solid'
                  value={choiceId}
                  onChange={(event) => setChoiceId(event.target.value)}
                >
                  <option value='detected'>همین دستگاه ({environmentLabel(detected)})</option>
                  {GUIDE_CHOICES.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {androidApp && (
              <>
                <AndroidAppCard />
                <h3 className='ia-section-title ia-or'>یا بدون دانلود، نسخهٔ وب را نصب کنید</h3>
              </>
            )}

            {oneClick && (
              <div className='ia-oneclick'>
                <button type='button' className='btn btn-primary' onClick={install} disabled={busy}>
                  <i className='las la-download' aria-hidden='true' />
                  {busy ? 'در انتظار تأیید مرورگر…' : 'نصب با یک کلیک'}
                </button>
                <span className='ia-note'>مرورگر یک بار از شما تأیید می‌خواهد.</span>
              </div>
            )}
            {outcome === 'dismissed' && (
              <div className='ia-state ia-state--warn' role='status'>
                <i className='las la-undo' aria-hidden='true' />
                نصب انجام نشد. هر وقت خواستید، با مراحل زیر نصب کنید.
              </div>
            )}

            {guide.message && <p className='ia-message'>{guide.message}</p>}

            {guide.steps.length > 0 && (
              <section aria-labelledby='ia-steps-title'>
                <h3 id='ia-steps-title' className='ia-section-title'>
                  {oneClick ? 'یا از منوی مرورگر:' : 'مراحل نصب'}
                </h3>
                <InstallGuideSteps steps={guide.steps} />
              </section>
            )}

            {guide.notes?.map((note, index) => (
              <p key={index} className='ia-note ia-note--tip'>
                <i className='las la-lightbulb' aria-hidden='true' />
                <span>
                  <GuideLine step={note} />
                </span>
              </p>
            ))}

            {(guide.support === 'other-browser' || guide.support === 'open-in-browser') && (
              <div className='ia-copy'>
                <input
                  className='form-control form-control-solid ia-copy__url'
                  value={panelUrl}
                  readOnly
                  dir='ltr'
                  aria-label='نشانی پنل'
                  onFocus={(event) => event.currentTarget.select()}
                />
                <button type='button' className='btn btn-light-primary' onClick={copyLink}>
                  <i className='las la-copy' aria-hidden='true' />
                  {copy === 'copied' ? 'کپی شد' : 'کپی لینک'}
                </button>
                {copy === 'failed' && (
                  <span className='ia-note' role='alert'>
                    کپی نشد؛ نشانی را از همین کادر انتخاب و کپی کنید.
                  </span>
                )}
              </div>
            )}

            {guide.steps.length > 0 && (
              <p className='ia-note'>پس از نصب، برنامه را از {guide.where} باز کنید.</p>
            )}
          </>
        )}

        {!detected.mobile && (
          <section className='ia-phone' aria-labelledby='ia-phone-title'>
            <div className='ia-phone__qr'>
              <QRCode value={panelUrl} size={116} quietZone={6} ecLevel='M' />
            </div>
            <div>
              <h3 id='ia-phone-title' className='ia-section-title'>
                روی گوشی هم نصب کنید
              </h3>
              <p className='ia-note'>
                این کد را با دوربین گوشی بخوانید تا پنل باز شود؛ پس از ورود، همین راهنما روش نصب
                همان گوشی را نشان می‌دهد.
              </p>
              {androidAppAvailable() && (
                <p className='ia-note'>
                  برنامهٔ اندروید:{' '}
                  <a href={ANDROID_APP.page} target='_blank' rel='noopener noreferrer'>
                    <bdi dir='ltr'>download.asatsp.ir/App</bdi>
                  </a>
                </p>
              )}
            </div>
          </section>
        )}

        <div className='ia-actions'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            بستن
          </button>
        </div>
      </Modal.Body>
    </Modal>
  )
}
